import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';

import {
  Box,
  Image,
  Text,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {PIM_SQUARE_THUMBNAIL_PX} from '@amblique/king-living/app/components/configuration-selector/utils';

const FOOTER_EMPTY_TITLE = {
  /** Main step rail — e.g. no cover until user opens picker */
  mainSteps: {
    id: 'customize_panel.footer.empty_title_main',
    defaultMessage: 'Choose your cover',
  },

  /** Nested product picker (cover swatches, option list) or choose-layout panel */
  nestedSelection: {
    id: 'customize_panel.footer.empty_title_nested',
    defaultMessage: 'No selection yet',
  },
};

/**
 * Shared sticky footer bar for customize steps.
 * Shows optional thumbnail, product name, price text, and action button(s) via children.
 */
export function SubtotalFooter({
  children,
  emptyTitleContext = 'mainSteps',
  footerProduct,
  priceText,
  selectionSummary,
  styles,
}) {
  const emptyTitle =
    FOOTER_EMPTY_TITLE[footerProduct?.emptyTitleContext ?? emptyTitleContext] ??
    FOOTER_EMPTY_TITLE.mainSteps;
  const summaryText = typeof selectionSummary === 'string' ? selectionSummary.trim() : '';
  const showSelectionSummary = Boolean(summaryText);

  return (
    <Box className="subtotal-footer" {...styles.subtotalFooter}>
      {footerProduct?.imageUrl && (
        <Image
          alt=""
          htmlHeight={PIM_SQUARE_THUMBNAIL_PX}
          htmlWidth={PIM_SQUARE_THUMBNAIL_PX}
          src={footerProduct.imageUrl}
          {...styles.subtotalThumbnail}
        />
      )}
      <Box className="subtotal-footer-info" {...styles.subtotalInfo}>
        {showSelectionSummary ? (
          <Text
            className="subtotal-footer__selection-summary"
            {...styles.subtotalSelectionSummary}
          >
            {summaryText}
          </Text>
        ) : (
          <Text className="subtotal-footer__title" {...styles.subtotalTitle}>
            {footerProduct?.name ?? <FormattedMessage {...emptyTitle} />}
          </Text>
        )}
        {priceText && (
          <Text className="subtotal-footer__price" {...styles.subtotalPriceSubtitle}>
            {priceText}
          </Text>
        )}
      </Box>
      {children}
    </Box>
  );
}

SubtotalFooter.displayName = 'SubtotalFooter';

SubtotalFooter.propTypes = {
  children: PropTypes.node.isRequired,
  emptyTitleContext: PropTypes.oneOf(['mainSteps', 'nestedSelection']),
  footerProduct: PropTypes.shape({
    canConfirmSlot: PropTypes.bool,
    emptyTitleContext: PropTypes.oneOf(['mainSteps', 'nestedSelection']),
    imageUrl: PropTypes.string,
    name: PropTypes.string,
  }),
  priceText: PropTypes.node,
  selectionSummary: PropTypes.string,
  styles: PropTypes.object.isRequired,
};
